// Centralized FontAwesome icons with Lucide-compatible API
// This provides consistent wellness-themed icons across the customer portal

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faHouse,
  faCalendarDays,
  faCalendarPlus,
  faSpa,
  faGear,
  faComments,
  faCreditCard,
  faVideo,
  faClock,
  faUsers,
  faCircleCheck,
  faCircleXmark,
  faDownload,
  faLocationDot,
  faXmark,
  faPlay,
  faMagnifyingGlass,
  faTrophy,
  faStar,
  faUser,
  faLock,
  faEye,
  faEyeSlash,
  faEnvelope,
  faChevronLeft,
  faChevronRight,
  faChevronDown,
  faChevronUp,
  faBell,
  faRightFromBracket,
  faBars,
  faSun,
  faMoon,
  faPaperPlane,
  faPenToSquare,
  faBullseye,
  faCircleQuestion,
  faFire,
  faAward,
  faArrowLeft,
  faArrowRight,
  faCircleExclamation,
  faSpinner,
  faCamera,
  faWandMagicSparkles,
  faCrown,
  faBolt,
  faArrowTrendUp,
  faTowerBroadcast,
  faBarcode,
  faQrcode,
  faBuilding,
  faPhone,
  faMessage,
  faThumbsUp,
  faRightToBracket,
  faUserPlus,
  faCircle,
  faCheck,
  faRing,
} from '@fortawesome/free-solid-svg-icons';

// Helper to create icon wrapper components with consistent sizing
const createIcon = (icon) => ({ className, style, ...props }) => (
  <FontAwesomeIcon
    icon={icon}
    fixedWidth
    className={className}
    style={style}
    {...props}
  />
);

// Navigation & Layout
export const Home = createIcon(faHouse);
export const Menu = createIcon(faBars);
export const X = createIcon(faXmark);
export const Settings = createIcon(faGear);
export const LogOut = createIcon(faRightFromBracket);
export const LogIn = createIcon(faRightToBracket);

// Calendar & Time
export const Calendar = createIcon(faCalendarDays);
export const CalendarPlus = createIcon(faCalendarPlus);
export const Clock = createIcon(faClock);

// Media & Content
export const Video = createIcon(faVideo);
export const Play = createIcon(faPlay);
export const Camera = createIcon(faCamera);
export const Download = createIcon(faDownload);

// Users & Community
export const User = createIcon(faUser);
export const Users = createIcon(faUsers);
export const UserPlus = createIcon(faUserPlus);
export const MessageCircle = createIcon(faMessage);
export const Send = createIcon(faPaperPlane);
export const ThumbsUp = createIcon(faThumbsUp);

// Status & Feedback
export const CheckCircle = createIcon(faCircleCheck);
export const XCircle = createIcon(faCircleXmark);
export const AlertCircle = createIcon(faCircleExclamation);
export const HelpCircle = createIcon(faCircleQuestion);
export const Circle = createIcon(faCircle);
export const Check = createIcon(faCheck);

// Navigation Arrows
export const ChevronLeft = createIcon(faChevronLeft);
export const ChevronRight = createIcon(faChevronRight);
export const ChevronDown = createIcon(faChevronDown);
export const ChevronUp = createIcon(faChevronUp);
export const ArrowLeft = createIcon(faArrowLeft);
export const ArrowRight = createIcon(faArrowRight);

// Form & Input
export const Mail = createIcon(faEnvelope);
export const Lock = createIcon(faLock);
export const Eye = createIcon(faEye);
export const EyeOff = createIcon(faEyeSlash);
export const Edit = createIcon(faPenToSquare);
export const Search = createIcon(faMagnifyingGlass);

// Location & Places
export const MapPin = createIcon(faLocationDot);
export const Building2 = createIcon(faBuilding);
export const Phone = createIcon(faPhone);

// Notifications & Communication
export const Bell = createIcon(faBell);
export const Comments = createIcon(faComments);

// Finance
export const CreditCard = createIcon(faCreditCard);

// Achievements & Gamification
export const Trophy = createIcon(faTrophy);
export const Award = createIcon(faAward);
export const Star = createIcon(faStar);
export const Crown = createIcon(faCrown);
export const Target = createIcon(faBullseye);
export const Flame = createIcon(faFire);
export const TrendingUp = createIcon(faArrowTrendUp);
export const Ring = createIcon(faRing);

// Special Effects
export const Sparkles = createIcon(faWandMagicSparkles);
export const Zap = createIcon(faBolt);
export const Radio = createIcon(faTowerBroadcast);
export const ScanLine = createIcon(faBarcode);
export const QrCode = createIcon(faQrcode);

// Theme
export const Sun = createIcon(faSun);
export const Moon = createIcon(faMoon);

// Loading
export const Loader = createIcon(faSpinner);
export const Loader2 = createIcon(faSpinner);

// Wellness (special)
export const Spa = createIcon(faSpa);

// Wifi icons - these don't have direct FA equivalents, keeping as custom
export const Wifi = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M5 12.55a11 11 0 0 1 14.08 0" />
    <path d="M1.42 9a16 16 0 0 1 21.16 0" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

export const WifiOff = ({ className, style }) => (
  <svg className={className} style={style} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <line x1="1" y1="1" x2="23" y2="23" />
    <path d="M16.72 11.06A10.94 10.94 0 0 1 19 12.55" />
    <path d="M5 12.55a10.94 10.94 0 0 1 5.17-2.39" />
    <path d="M10.71 5.05A16 16 0 0 1 22.58 9" />
    <path d="M1.42 9a15.91 15.91 0 0 1 4.7-2.88" />
    <path d="M8.53 16.11a6 6 0 0 1 6.95 0" />
    <line x1="12" y1="20" x2="12.01" y2="20" />
  </svg>
);

// Default export with all icons
export default {
  Home,
  Menu,
  X,
  Settings,
  LogOut,
  LogIn,
  Calendar,
  CalendarPlus,
  Clock,
  Video,
  Play,
  Camera,
  Download,
  User,
  Users,
  UserPlus,
  MessageCircle,
  Send,
  ThumbsUp,
  CheckCircle,
  XCircle,
  AlertCircle,
  HelpCircle,
  Circle,
  Check,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  ArrowLeft,
  ArrowRight,
  Mail,
  Lock,
  Eye,
  EyeOff,
  Edit,
  Search,
  MapPin,
  Building2,
  Phone,
  Bell,
  Comments,
  CreditCard,
  Trophy,
  Award,
  Star,
  Crown,
  Target,
  Flame,
  TrendingUp,
  Ring,
  Sparkles,
  Zap,
  Radio,
  ScanLine,
  QrCode,
  Sun,
  Moon,
  Loader,
  Loader2,
  Spa,
  Wifi,
  WifiOff,
};
